import ButtonBase from '@mui/material/ButtonBase';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import type { LucideIcon } from 'lucide-react';
import { useId } from 'react';
import type { CitedPoint } from '../api/schemas';
import { citeChipSx } from './PageCite';

type Props = { title: string; icon: LucideIcon; points: CitedPoint[]; onOpen: (page: number, passage: string) => void };

/** Key calls, drivers or risks: each point opens its page with the passage tinted. Hidden when empty. */
export function PointSection({ title, icon: Icon, points, onOpen }: Props) {
  const headingId = useId();
  if (points.length === 0) return null;
  return (
    <section aria-labelledby={headingId}>
      <Stack direction="row" sx={{ alignItems: 'center', gap: 0.75, mb: 0.75, color: 'text.secondary' }}>
        <Icon size={14} aria-hidden />
        <Typography id={headingId} variant="body2" component="h2" sx={{ fontWeight: 600, color: 'text.primary' }}>{title}</Typography>
        <Typography variant="caption">{points.length}</Typography>
      </Stack>
      <Stack component="ul" sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {points.map((p, i) => (
          <li key={p.text}>
            <ButtonBase onClick={() => { onOpen(p.page, p.text); }} aria-label={`${p.text} Open page ${p.page}`} sx={{ ...rowSx, animationDelay: `${i * 40}ms` }}>
              <Typography variant="body2" sx={{ flexGrow: 1, textAlign: 'left', lineHeight: 1.55 }}>{p.text}</Typography>
              <Typography component="span" sx={citeChipSx} aria-hidden>p.{p.page}</Typography>
            </ButtonBase>
          </li>
        ))}
      </Stack>
    </section>
  );
}

const rowSx = {
  width: '100%',
  alignItems: 'flex-start',
  gap: 1.5,
  px: 1,
  mx: -1,
  py: 1.25,
  borderRadius: 1,
  borderBottom: 1,
  borderColor: 'divider',
  transition: 'background-color 120ms',
  '&:hover': { bgcolor: 'surface.sidebar' },
  '@keyframes pointIn': { from: { opacity: 0, transform: 'translateY(3px)' }, to: { opacity: 1, transform: 'none' } },
  animation: 'pointIn 220ms ease-out both',
  '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
} as const;
